import React, { useState, useEffect, useCallback } from 'react';
import {
    useTranslate,
    useNotify,
    useRefresh,
    useDataProvider,
} from 'react-admin';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField as MuiTextField,
    Typography,
    Autocomplete,
    IconButton,
    Alert,
    Chip,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
} from '@mui/material';
import { Add as AddIcon, PersonRemove } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';

type MemberType = 'app_user' | 'rfid_user';

interface AppUser {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    numericUserId?: number;
}

interface RfidUser {
    id: string;
    displayName?: string;
    email?: string;
    phone?: string;
}

interface Member {
    id: string;
    type: MemberType;
    userId?: string;
    rfidUserId?: string;
    user?: AppUser;
    rfidUser?: RfidUser;
    addedAt: string;
    addedBy?: string;
}

interface MembersTabProps {
    groupId: string;
}

const fmtAppUser = (u: AppUser | undefined): string => {
    if (!u) return '-';
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    if (name && u.email) return `${name} (${u.email})`;
    if (u.email) return u.email;
    if (u.numericUserId) return `#${u.numericUserId}`;
    return u.id;
};

const fmtRfidUser = (u: RfidUser | undefined): string => {
    if (!u) return '-';
    if (u.displayName && u.email) return `${u.displayName} (${u.email})`;
    if (u.displayName) return u.displayName;
    if (u.email) return u.email;
    return u.id;
};

const fmtMember = (m: Member): string => {
    if (m.type === 'app_user') return fmtAppUser(m.user);
    return fmtRfidUser(m.rfidUser);
};

export const MembersTab: React.FC<MembersTabProps> = ({ groupId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [addType, setAddType] = useState<MemberType>('app_user');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const loadMembers = useCallback(async () => {
        if (!groupId) return;
        setLoading(true);
        try {
            const resp: any = await apiClient.get(`/api/backend/user-groups/${groupId}/members`);
            setMembers(resp.data || []);
        } catch {
            notify('resources.user-groups.notifications.error.update', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [groupId, notify]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    const searchUsers = useCallback(async (q: string) => {
        if (!q || q.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const resource = addType === 'app_user' ? 'app-users' : 'app-rfid-users';
            const resp = await dataProvider.getList(resource, {
                pagination: { page: 1, perPage: 10 },
                sort: { field: addType === 'app_user' ? 'email' : 'displayName', order: 'ASC' },
                filter: { q },
            });
            setSearchResults(resp.data);
        } catch {
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }, [dataProvider, addType]);

    useEffect(() => {
        const timer = setTimeout(() => {
            searchUsers(searchTerm);
        }, 250);
        return () => clearTimeout(timer);
    }, [searchTerm, searchUsers]);

    const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddType(event.target.value as MemberType);
        setSelectedUser(null);
        setSearchTerm('');
        setSearchResults([]);
    };

    const getOptionLabel = (option: any): string => {
        if (addType === 'app_user') return fmtAppUser(option);
        return fmtRfidUser(option);
    };

    const handleAddMember = async () => {
        if (!selectedUser) return;
        setSubmitting(true);
        try {
            const body = addType === 'app_user'
                ? { userId: selectedUser.id }
                : { rfidUserId: selectedUser.id };
            await apiClient.post(`/api/backend/user-groups/${groupId}/members`, body);
            notify('resources.user-groups.members.addedSuccess', { type: 'success' });
            setAddOpen(false);
            setSelectedUser(null);
            setSearchTerm('');
            await loadMembers();
            refresh();
        } catch (err: any) {
            const code = err?.body?.errorCode || err?.errorCode || '';
            if (code === 'DUPLICATE_MEMBER') {
                notify('resources.user-groups.members.duplicate', { type: 'warning' });
            } else {
                notify('resources.user-groups.notifications.error.update', { type: 'error' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveMember = async (m: Member) => {
        if (!window.confirm(translate('resources.user-groups.members.confirmRemove'))) return;
        try {
            const urlType = m.type === 'app_user' ? 'app-user' : 'rfid-user';
            const memberId = m.type === 'app_user' ? m.userId : m.rfidUserId;
            await apiClient.delete(`/api/backend/user-groups/${groupId}/members/${urlType}/${memberId}`);
            notify('resources.user-groups.members.removedSuccess', { type: 'success' });
            await loadMembers();
            refresh();
        } catch {
            notify('resources.user-groups.notifications.error.update', { type: 'error' });
        }
    };

    const handleDialogClose = () => {
        setAddOpen(false);
        setSelectedUser(null);
        setSearchTerm('');
        setSearchResults([]);
        setAddType('app_user');
    };

    const addButtonLabel = addType === 'app_user'
        ? translate('resources.user-groups.members.add_app_user')
        : translate('resources.user-groups.members.add_rfid_user');

    const searchLabel = addType === 'app_user'
        ? translate('resources.user-groups.members.searchUsers')
        : translate('resources.user-groups.members.searchRfidUsers');

    const selectPlaceholder = addType === 'app_user'
        ? translate('resources.user-groups.members.selectUser')
        : translate('resources.user-groups.members.selectRfidUser');

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.user-groups.members.title')}
                    </Typography>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setAddOpen(true)}
                    >
                        {translate('resources.user-groups.members.add')}
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : members.length === 0 ? (
                    <Alert severity="info">{translate('resources.user-groups.members.empty')}</Alert>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('resources.user-groups.members.memberType')}</TableCell>
                                    <TableCell>{translate('resources.user-groups.fields.userId')}</TableCell>
                                    <TableCell>{translate('resources.user-groups.fields.addedAt')}</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((m, idx) => (
                                    <TableRow key={m.id || idx}>
                                        <TableCell>
                                            <Chip
                                                label={translate(`resources.user-groups.members.type.${m.type}`)}
                                                size="small"
                                                variant="outlined"
                                                color={m.type === 'app_user' ? 'primary' : 'secondary'}
                                            />
                                        </TableCell>
                                        <TableCell>{fmtMember(m)}</TableCell>
                                        <TableCell>{m.addedAt ? new Date(m.addedAt).toLocaleString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveMember(m)}
                                                title={translate('resources.user-groups.members.remove')}
                                            >
                                                <PersonRemove fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>

            <Dialog open={addOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>{translate('resources.user-groups.members.add')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl>
                            <FormLabel sx={{ mb: 1 }}>
                                {translate('resources.user-groups.members.memberType')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={addType}
                                onChange={handleTypeChange}
                            >
                                <FormControlLabel
                                    value="app_user"
                                    control={<Radio size="small" />}
                                    label={translate('resources.user-groups.members.type.app_user')}
                                />
                                <FormControlLabel
                                    value="rfid_user"
                                    control={<Radio size="small" />}
                                    label={translate('resources.user-groups.members.type.rfid_user')}
                                />
                            </RadioGroup>
                        </FormControl>
                        <Autocomplete
                            options={searchResults}
                            loading={searchLoading}
                            getOptionLabel={getOptionLabel}
                            value={selectedUser}
                            onChange={(_, v) => setSelectedUser(v)}
                            onInputChange={(_, v) => setSearchTerm(v)}
                            renderInput={(params) => (
                                <MuiTextField
                                    {...params}
                                    label={searchLabel}
                                    placeholder={selectPlaceholder}
                                    fullWidth
                                />
                            )}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            noOptionsText={searchTerm.length < 2 ? translate('ra.navigation.no_results') : translate('ra.navigation.no_results')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={submitting}>
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddMember}
                        disabled={!selectedUser || submitting}
                    >
                        {submitting ? <CircularProgress size={16} /> : addButtonLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default MembersTab;
